import { css } from '@emotion/react';
import { ReactElement, ReactNode, useCallback, useRef, useState } from 'react';
import { memo, useMemo } from 'react';
import { ChatAttributeClassifier, ChatAttributeType } from 'sdk';

import { styled } from '../../../../styled';
import { Tooltip } from '../errorLabel';
import { Input } from '../input';
import { Label } from '../label';
import { Select } from '../select';

export type InitialValueType = number | string | ChatAttributeClassifier | ChatAttributeClassifier[];
export type SelectInitialValueType = ChatAttributeClassifier | ChatAttributeClassifier[];

const parseValue = (options: ChatAttributeClassifier[], value: string): SelectInitialValueType => {
	return !value.includes('|')
		? { key: value, title: options.find((v) => v.key === value)?.title || '' }
		: options.filter((v) => value.split('|').some((val) => val === v.key));
};

export type FieldInterface = {
	className: string;
	fieldName: string;
	usePhoneMask?: boolean;
	name: string;
	initialValue?: InitialValueType;
	required: boolean;
	type: ChatAttributeType;
	error?: string;
	icon?: ReactNode;
	attributes?: ChatAttributeClassifier[];
};

const styles = css`
	position: relative;

	& > div > div {
		display: flex;
		justify-content: center;
	}

	& > div {
		position: relative;
	}
`;

const Component = ({
	className,
	name,
	fieldName,
	required,
	type,
	attributes,
	initialValue,
	usePhoneMask,
	icon,
	error,
}: FieldInterface): ReactElement => {
	const fieldRef = useRef<HTMLDivElement>(null);
	const Component = useMemo(() => {
		switch (type) {
			case ChatAttributeType.Select:
			case ChatAttributeType.StringSelect:
			case ChatAttributeType.Rating:
			case ChatAttributeType.Collection:
			case ChatAttributeType.StringCollection: {
				return (
					<Select
						hasError={!!error}
						isMulti={ChatAttributeType.Collection === type || ChatAttributeType.StringCollection === type}
						name={name}
						initialValue={
							typeof initialValue === 'number' || typeof initialValue === 'string'
								? parseValue(attributes || [], (initialValue || '').toString())
								: (initialValue as SelectInitialValueType)
						}
						attributes={attributes || []}
					/>
				);
			}
			default: {
				return (
					<Input
						usePhoneMask={usePhoneMask}
						name={name}
						hasError={!!error}
						initialValue={initialValue as string}
						icon={icon}
					/>
				);
			}
		}
	}, [attributes, initialValue, name, type, icon, error, usePhoneMask]);

	const [showTooltip, setShowTooltip] = useState(false);
	const onFocusHandler = useCallback((): void => {
		if (error) {
			setShowTooltip(true);
		}
	}, [error]);
	const onBlurHandler = useCallback((): void => {
		if (showTooltip) {
			setShowTooltip(false);
		}
	}, [showTooltip]);
	return (
		<div className={className} onBlur={onBlurHandler} onFocus={onFocusHandler}>
			<Label hasError={!!error} text={fieldName} required={required} />
			<div onClick={onFocusHandler} ref={fieldRef}>
				{error && showTooltip && (
					<Tooltip fieldRef={fieldRef} setShowTooltip={setShowTooltip} className="error-tooltip">
						{error}
					</Tooltip>
				)}
				{Component}
			</div>
		</div>
	);
};

export const Field = styled(memo(Component))(styles);
